/* @flow */

import * as React from 'react';
import { Transition } from 'react-transition-group';
import { ENTERING } from 'react-transition-group/Transition';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { position } from 'polished';
import colorUtils from 'color';
import Tappable from 'app/components/commons/Tappable';
import { hover } from 'app/utils/cssUtils';

type Props = {
  className?: string,
  disabled?: boolean,
  onSelect?: () => void,
  type?: 'add' | 'captain' | 'lock' | 'remove' | 'selected' | 'unlock'
};

type State = {
  showTransition: boolean,
  transitionType?: string
};

const ADD = '"\\f489"';
const CIRCLE = '"\\f401"';
const REMOVE = '"\\f404"';
const TICK = '"\\f3fd"';

const defaultProps = {
  className: undefined,
  disabled: false,
  onSelect: undefined,
  type: 'add'
};

const selectingAnimation = keyframes({
  from: {
    transform: 'rotate(0deg)'
  },
  to: {
    transform: 'rotate(360deg)'
  }
});

const RowButtonComponent = styled.div(
  {
    alignItems: 'center',
    display: 'flex',
    fontSize: 28,
    justifyContent: 'center',
    lineHeight: 1,
    minWidth: 0,
    position: 'relative',
    textAlign: 'center',
    width: '2.5rem'
  },
  ({ disabled }) => ({
    opacity: disabled ? 0.25 : undefined
  }),
  ({ enableHover, type, theme }) => {
    switch (type) {
      case 'add':
        return {
          fontFamily: 'Ionicons',
          '&::before': {
            alignItems: 'center',
            display: 'flex',
            content: CIRCLE,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          '&::after': {
            alignItems: 'center',
            display: 'flex',
            content: ADD,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          ...hover(
            enableHover
              ? {
                  color: theme.color.primary
                }
              : {}
          )
        };
      case 'captain':
        return {
          color: theme.color.blue,
          '&::before': {
            content: '"\\f21b"',
            fontFamily: 'Ionicons'
          },
          '&::after': {
            alignItems: 'center',
            content: '"C"',
            color: theme.color.white,
            display: 'flex',
            fontSize: '0.5em',
            height: '100%',
            lineHeight: 2,
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0
          },
          ...hover(
            enableHover
              ? {
                  color: theme.color.basis4
                }
              : {}
          )
        };
      case 'remove':
        return {
          color: theme.color.danger,
          fontFamily: 'Ionicons',
          '&::before': {
            alignItems: 'center',
            display: 'flex',
            content: CIRCLE,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          '&::after': {
            alignItems: 'center',
            display: 'flex',
            content: REMOVE,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          ...hover(
            enableHover
              ? {
                  backgroundColor: theme.color.danger,
                  color: theme.color.white
                }
              : {}
          )
        };
      case 'selected':
        return {
          backgroundColor: theme.color.primary,
          color: theme.color.white,
          fontFamily: 'Ionicons',
          '&::before': {
            alignItems: 'center',
            display: 'flex',
            content: CIRCLE,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          '&::after': {
            alignItems: 'center',
            display: 'flex',
            content: TICK,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          ...hover(
            enableHover
              ? {
                  backgroundColor: theme.color.danger,
                  '&::after': {
                    content: REMOVE
                  }
                }
              : {}
          )
        };
      case 'selecting':
        return {
          backgroundColor: theme.color.primary,
          color: theme.color.white,
          fontFamily: 'Ionicons',
          '&::before': {
            alignItems: 'center',
            display: 'flex',
            content: CIRCLE,
            justifyContent: 'center',
            ...position('absolute', 0, 0)
          },
          '&::after': {
            alignItems: 'center',
            animationDuration: '0.5s',
            animationFillMode: 'forwards',
            animationName: selectingAnimation,
            display: 'flex',
            content: TICK,
            justifyContent: 'center',
            fontFamily: 'Ionicons',
            ...position('absolute', 0, 0)
          }
        };
      case 'lock':
        return {
          color: theme.color.primary,
          fontSize: 24,
          '&::before': {
            content: '"\\f200"',
            fontFamily: 'Ionicons'
          },
          ...hover(
            enableHover
              ? {
                  color: colorUtils(theme.color.primary)
                    .darken(0.2)
                    .string()
                }
              : {}
          )
        };
      case 'unlock':
        return {
          color: theme.color.basis6,
          fontSize: 24,
          '&::before': {
            content: '"\\f254"',
            fontFamily: 'Ionicons'
          },
          ...hover(
            enableHover
              ? {
                  color: theme.color.basis5
                }
              : {}
          )
        };
      default:
        return {};
    }
  }
);

class RowButton extends React.Component<Props, State> {
  props: Props;

  state: State = {
    showTransition: false
  };

  handleClick = () => {
    const { disabled, onSelect } = this.props;

    if (!disabled && onSelect) {
      this.setState({
        showTransition: true
      });
      onSelect();
    }
  };

  handleEntered = () => {
    this.setState({
      showTransition: false
    });
  };

  render() {
    const { className, disabled, type } = this.props;
    const { showTransition } = this.state;

    return (
      <Transition
        in={showTransition}
        onEntered={this.handleEntered}
        timeout={{
          enter: 5000,
          exit: 0
        }}
      >
        {state => (
          <Tappable
            component={RowButtonComponent}
            className={className}
            type={state === ENTERING && type === 'selected' ? 'selecting' : type}
            disabled={disabled}
            onTap={this.handleClick}
            enableHover={!disabled && state !== ENTERING}
            stopPropagation
          />
        )}
      </Transition>
    );
  }
}

RowButton.defaultProps = defaultProps;

export default RowButton;
